import { ArrowRight, FlaskConical } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { isModeAllowed, useMode } from '@/lib/mode/ModeProvider';
import { cn } from '@/lib/utils';

/**
 * Full-width amber chrome that pins to the top of the viewport while the
 * user is in sandbox mode. Mirrors Stripe's "TEST DATA" bar — quiet but
 * always-visible reminder that this view is hitting Adyen Test and the
 * data here doesn't touch real money.
 *
 * Renders nothing in production mode (zero chrome cost in the common case).
 * The right-aligned "Switch to Production" CTA is hidden for users whose
 * entitlement doesn't include production — they have nowhere to switch to.
 */
export function SandboxBanner({ className }: { className?: string }) {
  const { mode, setMode, allowed, isSwitching } = useMode();

  if (mode !== 'sandbox') return null;

  const canSwitchToProd = isModeAllowed(allowed, 'production');

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="sandbox-banner"
      className={cn(
        // Amber chrome, dark text. Subtle horizontal stripe pattern via a
        // repeating linear gradient so the bar reads as "chrome" rather
        // than a one-off alert. Stripe-flavoured: amber that doesn't shout.
        'relative flex w-full items-center justify-between gap-3 border-b border-amber-300/70 bg-amber-100 px-4 py-1.5 text-xs font-medium text-amber-900 dark:border-amber-700/60 dark:bg-amber-950 dark:text-amber-200',
        className,
      )}
      style={{
        // Stripe-style very low-contrast diagonal pattern. Inline so we
        // don't need a Tailwind plugin and so the pattern colour adapts
        // through `currentColor` mixing with the amber bg.
        backgroundImage:
          'repeating-linear-gradient(45deg, transparent 0 14px, rgba(0,0,0,0.025) 14px 15px)',
      }}
    >
      <div className="flex min-w-0 items-center gap-2">
        <FlaskConical aria-hidden className="h-3.5 w-3.5 shrink-0" />
        <p className="truncate">
          You&apos;re in <strong className="font-semibold">Sandbox mode</strong> — data here
          doesn&apos;t touch real money.
        </p>
      </div>

      {canSwitchToProd ? (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={isSwitching}
          onClick={() => void setMode('production')}
          className="h-6 gap-1 px-2 text-xs text-amber-900 hover:bg-amber-200/70 hover:text-amber-950 dark:text-amber-200 dark:hover:bg-amber-900/60 dark:hover:text-amber-100"
        >
          Switch to Production
          <ArrowRight aria-hidden className="h-3 w-3" />
        </Button>
      ) : null}
    </div>
  );
}
